"use client";

import * as React from "react";
import { LogFilters } from "@/components/logs/log-filters";
import { useLogFilters } from "@/hooks/use-log-filters";
import { useDashboardAnalytics } from "@/hooks/use-dashboard-analytics";
import { UrlFiltersProvider } from "@/hooks/use-url-filters";
import { CircularLoading } from "@/components/shared/circular-loading";
import { DashboardSummary } from "./dashboard-summary";
import { RequestTrendChart } from "./request-trend-chart";
import { ErrorTrendChart } from "./error-trend-chart";
import { RouteErrorChart } from "./route-error-chart";
import { UserErrorChart } from "./user-error-chart";
import { MethodBreakdownChart } from "./method-breakdown-chart";
import { StatusCodeChart } from "./status-code-chart";
import { getDefaultAnalyticsRange, parseFlexibleDate } from "@/utils/date-utils";

function DashboardClientInner() {
  const {
    filters,
    setFilters,
    setFiltersLocal,
    toggleRoute,
    toggleUser,
    toggleMethod,
    toggleStatusCode,
    toggleDateBucket,
    isPending,
  } = useLogFilters();

  // Seed Created = last 2 days in memory only — do not put default timestamps in the URL.
  React.useEffect(() => {
    if (filters.lookbackAll) return;
    if (filters.createdFrom || filters.createdTo || filters.updatedFrom || filters.updatedTo) {
      return;
    }
    const { createdFrom, createdTo } = getDefaultAnalyticsRange();
    setFiltersLocal({ createdFrom, createdTo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.createdFrom, filters.createdTo, filters.updatedFrom, filters.updatedTo, filters.lookbackAll]);

  // Drop legacy default-only createdAt query params from the address bar once.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const keys = [...params.keys()];
    if (keys.length === 0) return;
    const onlyDefaultCreated = keys.every((key) => key === "createdFrom" || key === "createdTo");
    if (!onlyDefaultCreated) return;
    const createdFrom = filters.createdFrom ?? parseFlexibleDate(params.get("createdFrom") ?? undefined) ?? undefined;
    const createdTo = filters.createdTo ?? parseFlexibleDate(params.get("createdTo") ?? undefined) ?? undefined;
    if (createdFrom || createdTo) {
      setFiltersLocal({
        ...(createdFrom ? { createdFrom } : {}),
        ...(createdTo ? { createdTo } : {}),
      });
    }
    window.history.replaceState(null, "", window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    summary,
    trends,
    breakdowns,
    isSummaryLoading,
    isChartsLoading,
    error,
    refetch,
  } = useDashboardAnalytics(filters);

  const waitingForDefaultDates =
    !filters.createdFrom && !filters.createdTo && !filters.updatedFrom && !filters.updatedTo;
  const filtersReady = !waitingForDefaultDates && !isPending;
  const metricsReady = Boolean(summary);
  const chartsReady = Boolean(trends && breakdowns) && !isChartsLoading;

  return (
    <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
      <LogFilters />

      <div className="relative min-h-[28rem] space-y-6">
        {!filtersReady ? (
          <div className="relative flex min-h-[12rem] items-center justify-center">
            <CircularLoading label="Preparing filters…" size="md" />
          </div>
        ) : (
          <>
            <section aria-label="Dashboard metrics" className="relative min-h-[8rem]">
              {isSummaryLoading && !summary ? (
                <div className="relative flex min-h-[10rem] items-center justify-center rounded-lg border border-dashed">
                  <CircularLoading label="Loading metrics…" size="md" />
                </div>
              ) : (
                <DashboardSummary
                  summary={summary}
                  isLoading={false}
                  error={error && !summary ? error : null}
                  onRetry={refetch}
                />
              )}
            </section>

            {metricsReady ? (
              <section aria-label="Dashboard charts" className="space-y-3">
                <h2 className="text-sm font-semibold">Trends</h2>
                {isChartsLoading || !chartsReady ? (
                  <div className="relative flex min-h-[20rem] items-center justify-center rounded-lg border border-dashed">
                    <CircularLoading label="Loading charts…" size="md" />
                  </div>
                ) : (
                  <div className="grid gap-4 lg:grid-cols-2">
                    <RequestTrendChart
                      points={trends?.points ?? []}
                      granularity="day"
                      isLoading={false}
                      onSelectBucket={toggleDateBucket}
                    />
                    <ErrorTrendChart
                      points={trends?.points ?? []}
                      granularity="day"
                      isLoading={false}
                      onSelectBucket={toggleDateBucket}
                    />
                    <RouteErrorChart
                      entries={breakdowns?.byRoute ?? []}
                      activeRoutes={filters.routes}
                      isLoading={false}
                      onSelectRoute={toggleRoute}
                    />
                    <UserErrorChart
                      entries={breakdowns?.byUser ?? []}
                      activeUsers={filters.userIds}
                      isLoading={false}
                      onSelectUser={toggleUser}
                    />
                    <MethodBreakdownChart
                      entries={breakdowns?.byMethod ?? []}
                      activeMethods={filters.methods}
                      isLoading={false}
                      onSelectMethod={toggleMethod}
                    />
                    <StatusCodeChart
                      entries={breakdowns?.byStatusCode ?? []}
                      activeStatusCodes={filters.statusCodes}
                      isLoading={false}
                      onSelectStatusCode={toggleStatusCode}
                    />
                  </div>
                )}
              </section>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export function DashboardClient() {
  return (
    <UrlFiltersProvider>
      <DashboardClientInner />
    </UrlFiltersProvider>
  );
}
